// these hooks are complicated, its still in flux
/* eslint-disable no-unused-vars */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isDesignCanvasProject } from '../../../config/designMode';
import { stubForType } from '../../../config/packageStubs';
import { isVerticalFlowType } from '../utils/layoutDag';
import { useAuth } from '../../../contexts/Auth';
import useApi from '../../../hooks/useAPI';
import {
  applyInheritedRegionToForm,
  inheritRegionFromUpstream,
} from '../utils/inheritRegion';
import {
  deleteCascadePhases,
  isBusyStatus,
  nodeDeployStatus,
} from '../utils/cascadeGraph';
import {
  runDesignDeleteCascade,
  runDesignDeployUntil,
  runDesignDestroyFrom,
} from '../utils/designHarness';
import {
  designActivatePatch,
  designScriptResult,
  downstreamScriptIds,
  startDesignTimer,
  stopDesignTimer,
} from '../utils/workflowRun';
import transformPackagesToNodes from './transform';

const useNodeOperations = (
  projectId,
  projectData,
  nodes,
  setNodes,
  edges,
  setEdges,
  reactFlowWrapper
) => {
  const { token } = useAuth();
  const { projects: projectsApi } = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const harnessBusy = useRef(false);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  useEffect(() => {
    if (
      projectData &&
      projectData.packages &&
      projectData.connections &&
      reactFlowWrapper.current
    ) {
      const canvasWidth = reactFlowWrapper.current.offsetWidth;
      const canvasHeight = reactFlowWrapper.current.offsetHeight;
      const { nodes: transformedNodes, edges: transformedEdges } =
        transformPackagesToNodes(
          projectData.packages,
          projectData.connections,
          canvasWidth,
          canvasHeight
        );
      setNodes(transformedNodes);
      setEdges(transformedEdges);
    }
  }, [projectData, setNodes, setEdges, reactFlowWrapper]);

  const onOpenModal = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      console.log('node', node);
      if (node) {
        const inherited = inheritRegionFromUpstream(node, nodes, edges);
        const applied = applyInheritedRegionToForm(
          node.data.parameters || {},
          node.data.parameter_data || {},
          inherited
        );
        return {
          selectedNodeId: nodeId,
          formData: applied.formData,
          schema: applied.schema,
        };
      }
      return null;
    },
    [nodes, edges]
  );

  const onSubmitForm = useCallback(
    async (nodeId, newFormData) => {
      try {
        console.log('useNodeOperations - Submitting form data:', newFormData);
        console.log('useNodeOperations - Node ID:', nodeId);
        console.log('useNodeOperations - Project ID:', projectId);
        const existing =
          nodes.find((n) => n.id === nodeId)?.data?.parameter_data || {};
        const merged = { ...existing, ...(newFormData || {}) };
        if (isDesignCanvasProject(projectId)) {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      parameter_data: merged,
                    },
                  }
                : node
            )
          );
          return merged;
        }
        const response = await projectsApi.updatePackage(
          projectId,
          nodeId,
          { parameter_data: merged },
          token
        );
        console.log('useNodeOperations - API response:', response);
        const updatedPackage = response.body;
        // Prefer server payload, but always merge what we saved so the canvas
        // stays in sync even when the client body is empty or omits fields.
        const parameterData =
          updatedPackage?.parameter_data ?? merged;

        setNodes((nds) =>
          nds.map((node) =>
            node.id === nodeId
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    parameter_data: parameterData,
                    ...(updatedPackage?.parameters != null
                      ? { parameters: updatedPackage.parameters }
                      : {}),
                  },
                }
              : node
          )
        );

        console.log('useNodeOperations - Updated package:', updatedPackage);
        return parameterData;
      } catch (error) {
        console.error('useNodeOperations - Error updating package:', error);
        throw error;
      }
    },
    [projectId, token, projectsApi, setNodes, nodes]
  );

  const onDeleteNode = useCallback(
    async (nodeId) => {
      try {
        const snapshotNodes = nodesRef.current;
        const snapshotEdges = edgesRef.current;
        const node = snapshotNodes.find((n) => n.id === nodeId);
        if (!node) return false;
        if (isBusyStatus(nodeDeployStatus(node))) {
          throw new Error('Cannot delete while this package is busy.');
        }
        if (harnessBusy.current) {
          throw new Error('A provision or delete walk is already running.');
        }

        if (isDesignCanvasProject(projectId)) {
          harnessBusy.current = true;
          try {
            stopDesignTimer(nodeId);
            await runDesignDeleteCascade({
              rootId: nodeId,
              nodes: snapshotNodes,
              edges: snapshotEdges,
              setNodes,
              setEdges,
            });
          } finally {
            harnessBusy.current = false;
          }
          return true;
        }

        const { order, edgeIds } = deleteCascadePhases(nodeId, snapshotEdges);
        const stillProvisioned = order.some((id) => {
          const n = snapshotNodes.find((x) => x.id === id);
          return n && nodeDeployStatus(n) !== 'NOT_DEPLOYED' && nodeDeployStatus(n) !== 'FAILED';
        });
        if (stillProvisioned) {
          throw new Error(
            'Destroy downstream packages first, then delete this card.'
          );
        }
        for (const id of order) {
          await projectsApi.deletePackage(projectId, id, token);
        }
        const edgeSet = new Set(edgeIds);
        const gone = new Set(order);
        setNodes((nds) => nds.filter((n) => !gone.has(n.id)));
        setEdges((eds) => eds.filter((e) => !edgeSet.has(e.id)));
        return true;
      } catch (error) {
        console.error('Error deleting node:', error);
        throw error;
      }
    },
    [projectId, token, projectsApi, setNodes, setEdges]
  );

  // todo: dedupe with CustomNode/NodeHeader.js
  const onDeploy = useCallback(
    async (nodeId) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      try {
        if (isDesignCanvasProject(projectId)) {
          if (harnessBusy.current) return false;
          harnessBusy.current = true;
          try {
            await runDesignDeployUntil({
              leafId: nodeId,
              nodes: nodesRef.current,
              edges: edgesRef.current,
              setNodes,
            });
          } finally {
            harnessBusy.current = false;
          }
          return true;
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, deploy_status: 'DEPLOYING' } }
              : n
          )
        );
        const response = await projectsApi.deployPackage(
          projectId,
          node.data.id,
          node.data.parameters,
          token
        );
        const updatedPackage = response.body;

        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    deploy_status: updatedPackage.deploy_status,
                    parameters: updatedPackage.parameter_data,
                    ...updatedPackage,
                  },
                }
              : n
          )
        );
        return true;
      } catch (error) {
        console.error('Error deploying package:', error);
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, deploy_status: 'FAILED' } }
              : n
          )
        );
        return false;
      }
    },
    [projectId, token, projectsApi, setNodes]
  );

  const onDestroy = useCallback(
    async (nodeId) => {
      try {
        if (isDesignCanvasProject(projectId)) {
          if (harnessBusy.current) return false;
          harnessBusy.current = true;
          try {
            await runDesignDestroyFrom({
              rootId: nodeId,
              nodes: nodesRef.current,
              edges: edgesRef.current,
              setNodes,
            });
          } finally {
            harnessBusy.current = false;
          }
          return true;
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, deploy_status: 'DESTROYING' } }
              : n
          )
        );
        const response = await projectsApi.destroyPackage(
          projectId,
          nodeId,
          token
        );
        const updatedPackage = response.body;
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    deploy_status: updatedPackage?.deploy_status || 'NOT_DEPLOYED',
                    output_data: {},
                  },
                }
              : n
          )
        );
        return true;
      } catch (error) {
        console.error('Error destroying package:', error);
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, deploy_status: 'FAILED' } }
              : n
          )
        );
        return false;
      }
    },
    [projectId, token, projectsApi, setNodes]
  );

  const updateNodeData = useCallback(
    (nodeId, newData) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return { ...node, data: { ...node.data, ...newData } };
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  //   class ProjectPackageBase(BaseModel):
  //     name: str
  //     type: str
  //     inputs: Dict[str, Any]
  //     outputs: Dict[str, Any]
  //     parameters: Dict[str, Any]

  // class ProjectPackageCreate(ProjectPackageBase):
  //     project_id: UUID4

  const createNode = useCallback(
    async (packageInfo, position) => {
      try {
        console.log('createNode - packageInfo:', packageInfo);
        if (isDesignCanvasProject(projectId)) {
          const id = crypto.randomUUID();
          const stub = stubForType(packageInfo.type);
          const createdPackage = {
            id,
            name: packageInfo.name,
            type: packageInfo.type,
            inputs: stub?.inputs || packageInfo.inputs || {},
            outputs: stub?.outputs || packageInfo.outputs || {},
            parameters: stub?.parameters || packageInfo.parameters || {},
            parameter_data: {},
            deploy_status: 'NOT_DEPLOYED',
            output_data: {},
          };
          return {
            id: createdPackage.id,
            type: 'custom',
            position,
            deletable: false,
            data: {
              id: createdPackage.id,
              label: createdPackage.name,
              type: createdPackage.type,
              inputs: createdPackage.inputs,
              outputs: createdPackage.outputs,
              parameters: createdPackage.parameters,
              parameter_data: createdPackage.parameter_data || {},
              deploy_status: 'NOT_DEPLOYED',
              output_data: {},
              flowVertical: isVerticalFlowType(createdPackage.type),
            },
          };
        }
        const stub = stubForType(packageInfo.type);
        const response = await projectsApi.createPackage(
          projectId,
          {
            name: packageInfo.name,
            type: packageInfo.type,
            inputs: stub?.inputs || packageInfo.inputs,
            outputs: stub?.outputs || packageInfo.outputs,
            parameters: stub?.parameters || packageInfo.parameters,
            deploy_status: 'NOT_DEPLOYED',
          },
          token
        );
        const createdPackage = response.body;

        const newNode = {
          id: createdPackage.id,
          type: 'custom',
          position: position,
          deletable: false,
          data: {
            id: createdPackage.id,
            label: createdPackage.name,
            type: createdPackage.type,
            inputs: createdPackage.inputs,
            outputs: createdPackage.outputs,
            parameters: createdPackage.parameters,
            parameter_data: createdPackage.parameter_data || {},
            deploy_status: 'NOT_DEPLOYED',
            flowVertical: isVerticalFlowType(createdPackage.type),
          },
        };

        return newNode;
      } catch (error) {
        console.error('Error creating package:', error);
        throw error;
      }
    },
    [projectId, token, projectsApi]
  );

  const patchNode = useCallback(
    (nodeId, patch) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n
        )
      );
    },
    [setNodes]
  );

  const fireDesignDownstream = useCallback(
    (timerId) => {
      const snapshotNodes = nodesRef.current;
      const snapshotEdges = edgesRef.current;
      const scripts = downstreamScriptIds(timerId, snapshotEdges, snapshotNodes);
      setNodes((nds) =>
        nds.map((n) => {
          if (!scripts.includes(n.id)) return n;
          return { ...n, data: { ...n.data, ...designScriptResult(n.data, { trigger: 'timer' }) } };
        })
      );
    },
    [setNodes]
  );

  const onRunNow = useCallback(
    async (nodeId) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      if (isDesignCanvasProject(projectId)) {
        patchNode(nodeId, designScriptResult(node.data, { trigger: 'manual' }));
        return;
      }
      await projectsApi.runWorkflow(projectId, nodeId, token);
      patchNode(nodeId, {
        output_data: {
          ...(node.data?.output_data || {}),
          last_run: {
            status: 'running',
            at: new Date().toISOString(),
            trigger: 'manual',
          },
        },
      });
    },
    [projectId, token, projectsApi, patchNode]
  );

  const onActivate = useCallback(
    async (nodeId) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      if (isDesignCanvasProject(projectId)) {
        patchNode(nodeId, designActivatePatch(node.data, true));
        startDesignTimer(nodeId, () => fireDesignDownstream(nodeId));
        return;
      }
      const response = await projectsApi.activateTimer(projectId, nodeId, token);
      const body = response?.body;
      patchNode(nodeId, {
        parameter_data: body?.parameter_data || {
          ...(node.data?.parameter_data || {}),
          active: true,
        },
      });
    },
    [projectId, token, projectsApi, patchNode, fireDesignDownstream]
  );

  const onDeactivate = useCallback(
    async (nodeId) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      if (isDesignCanvasProject(projectId)) {
        stopDesignTimer(nodeId);
        patchNode(nodeId, designActivatePatch(node.data, false));
        return;
      }
      const response = await projectsApi.deactivateTimer(projectId, nodeId, token);
      const body = response?.body;
      patchNode(nodeId, {
        parameter_data: body?.parameter_data || {
          ...(node.data?.parameter_data || {}),
          active: false,
        },
      });
    },
    [projectId, token, projectsApi, patchNode]
  );

  return {
    nodes,
    setNodes,
    onOpenModal,
    onSubmitForm,
    onDeleteNode,
    onDeploy,
    onDestroy,
    onRunNow,
    onActivate,
    onDeactivate,
    updateNodeData,
    createNode,
    isLoading,
    error,
  };
};

export default useNodeOperations;
